import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { SocketKey } from '@/Config/api/socketKey';
import { useSocket } from '@/Domain/components/SocketProvider';

type KbUsagePayload = {
  scenario_id: string;
  articles_used: number;
  articles_limit: number | null;
  can_add_article: boolean;
};

/**
 * Live-updates the cached `subscription` query whenever the backend
 * broadcasts `knowledge_base_updated`. The event payload carries the
 * already-computed usage snapshot (articles_used / articles_limit /
 * can_add_article), so the frontend writes directly to the cache via
 * `setQueryData` instead of triggering a refetch of `/allowed_features`.
 *
 * Why this matters: under a Firecrawl burst (100+ crawl.page webhooks in
 * <5s) the previous refetch-based approach had to either hammer the API
 * once per event or throttle and feel laggy. setQueryData is synchronous,
 * dependency-free, and updates as fast as the events arrive, so the
 * page-header bar ticks in lockstep with the scrape progress widget.
 */
export const useKbUsageSocket = (): void => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();

  useEffect(() => {
    const handler = (data: KbUsagePayload) => {
      queryClient.setQueryData<Record<string, unknown> | undefined>(['subscription'], (old) => {
        if (!old) return old;
        return {
          ...old,
          articles_used: data.articles_used,
          articles_limit: data.articles_limit,
          can_add_article: data.can_add_article,
        };
      });
    };
    socket.on(SocketKey.knowledgeBase.knowledgeBaseUpdated, handler);
    return () => {
      socket.off(SocketKey.knowledgeBase.knowledgeBaseUpdated, handler);
    };
  }, [queryClient, socket]);
};
